/**
 * Applying the server's bookmark events to the cached query data.
 *
 * Contract (`service/routes_events.py`): every committed mutation is announced
 * once — a row event for a single-row change, one `bookmark.resync` per batch
 * request. A row event is applied exactly wherever the client can evaluate it:
 * the default list (every live row, `date DESC, id DESC`) takes creates,
 * patches and deletes; filtered and search windows take patches and removals
 * of rows they hold, and are invalidated for membership, which is a server
 * predicate. An event for a row no window holds is not a sign of a missed
 * event: membership changes are always announced, so the row simply sits on a
 * page that was never loaded.
 *
 * An event is not a fetch. It is written without touching a window's
 * freshness, and every read in flight takes it too, so pages that left the
 * server before the change cannot land over it. This client's own optimistic
 * changes land the same way, ahead of the server's answer.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { BookmarkEvent, BookmarkListItem } from 'markcat-domain';
import { BOOKMARK_STATUSES } from 'markcat-domain';
import {
  BOOKMARKS_QUERY_KEY, correctReadsInFlight, invalidateBookmarkData, invalidateWindows,
  type BookmarkKey, type BookmarkListQueryData,
} from './useBookmarks';
import { CATEGORY_META_QUERY_KEY } from './useCategories';

const STATUSES: ReadonlySet<string> = new Set(BOOKMARK_STATUSES);
const TEXT_FIELDS = [
  'display_title', 'display_summary', 'category_path', 'domain',
  'extraction_method', 'content_type', 'last_error',
] as const;
const NUMBER_FIELDS = [
  'date', 'updated_at', 'pdf_pages', 'has_translations', 'has_screenshot', 'screenshot_version',
  'has_og_image', 'og_image_version', 'has_favicon', 'favicon_version',
  'is_broken_link', 'is_bot_protected', 'is_dead', 'is_favourite',
] as const;

/** The list-row fields an event carries. SSE payloads are a boundary, so each
 *  field is taken only with its wire type. An absent key keeps the cached
 *  value; a null clears it (a re-save resets `category_path`, for one). */
function rowFields(event: BookmarkEvent): Partial<BookmarkListItem> {
  const wire = event as unknown as Record<string, unknown>;
  const fields: Record<string, unknown> = {};
  for (const key of TEXT_FIELDS) {
    if (wire[key] === null || typeof wire[key] === 'string') fields[key] = wire[key];
  }
  for (const key of NUMBER_FIELDS) {
    if (wire[key] === null || typeof wire[key] === 'number') fields[key] = wire[key];
  }
  if (typeof wire.url === 'string' && wire.url) fields.url = wire.url;
  if (typeof wire.status === 'string' && STATUSES.has(wire.status)) fields.status = wire.status;
  if (wire.summary_is_fallback === null || typeof wire.summary_is_fallback === 'boolean') {
    fields.summary_is_fallback = wire.summary_is_fallback;
  }
  const confidence = wire.confidence;
  if (confidence === null || (typeof confidence === 'number' && confidence >= 0 && confidence <= 1)) {
    fields.confidence = confidence;
  }
  return fields as Partial<BookmarkListItem>;
}

const isDefaultList = (key: QueryKey) =>
  key.length === BOOKMARKS_QUERY_KEY.length && BOOKMARKS_QUERY_KEY.every((part, i) => key[i] === part);

/** The default order is `(date DESC, id DESC)`: true when `a` sorts above `b`. */
function sortsAbove(a: BookmarkKey, b: BookmarkKey): boolean {
  const aDate = a.date ?? 0;
  const bDate = b.date ?? 0;
  return aDate > bDate || (aDate === bDate && a.id > b.id);
}

/** A key past the loaded tail of a partial window belongs to a page never loaded. */
function pastTail(window: BookmarkListQueryData, key: BookmarkKey): boolean {
  return window.has_more && (!window.tail || sortsAbove(window.tail, key));
}

const count = (window: BookmarkListQueryData) => window.total_count ?? window.items.length;

/**
 * One row event's effect on one window: the window itself when it changes
 * nothing, otherwise the changed window. `serverCount` marks a window read's
 * result, whose count the server sent. A create or delete past the tail of a
 * partial default list only moves the count, since the row is on a page never
 * loaded. The cache has seen every earlier event, so it can move the count. A
 * server count may or may not hold the change already, so there the answer is
 * null: read again.
 */
type RowChange = (window: BookmarkListQueryData, isDefault: boolean, serverCount: boolean) => BookmarkListQueryData | null;

function insertCreated(window: BookmarkListQueryData, row: BookmarkListItem, serverCount: boolean) {
  // Held already: this client's own echo, a duplicate delivery, or a read that saw it.
  if (window.items.some((b) => b.id === row.id)) return window;
  // Past the tail it belongs to a page not loaded yet, which brings it in order.
  if (pastTail(window, row)) return serverCount ? null : { ...window, total_count: count(window) + 1 };
  const at = window.items.findIndex((b) => sortsAbove(row, b));
  const items = at < 0 ? [...window.items, row] : [...window.items.slice(0, at), row, ...window.items.slice(at)];
  return { ...window, items, total_count: count(window) + 1 };
}

function removeDeleted(
  window: BookmarkListQueryData,
  id: string,
  date: number | undefined,
  isDefault: boolean,
  serverCount: boolean,
) {
  if (window.items.some((b) => b.id === id)) {
    return { ...window, items: window.items.filter((b) => b.id !== id), total_count: count(window) - 1 };
  }
  // Absent from the default list: past the tail it was a member on a page never
  // loaded; within the loaded range it is already gone (this client's own delete
  // echoed, or a read that no longer saw it).
  if (isDefault && date !== undefined && pastTail(window, { id, date })) {
    return serverCount ? null : { ...window, total_count: count(window) - 1 };
  }
  return window;
}

function patchRow(window: BookmarkListQueryData, id: string, fields: Partial<BookmarkListItem>) {
  const at = window.items.findIndex((b) => b.id === id);
  if (at < 0) return window;
  const items = window.items.slice();
  items[at] = { ...items[at], ...fields };
  return { ...window, items };
}

function rowChange(event: BookmarkEvent, id: string): RowChange {
  const fields = rowFields(event);
  if (event.eventType === 'bookmark.created') {
    const row = fields.url && fields.status && typeof fields.date === 'number'
      ? { ...fields, id, url: fields.url, status: fields.status } : null;
    return (window, isDefault, serverCount) => (row && isDefault ? insertCreated(window, row, serverCount) : window);
  }
  if (event.eventType === 'bookmark.deleted') {
    const date = fields.date === null ? 0 : fields.date;
    return (window, isDefault, serverCount) => removeDeleted(window, id, date, isDefault, serverCount);
  }
  return (window) => patchRow(window, id, fields);
}

/**
 * Write one change into every cached bookmark window and every read in flight.
 *
 * Windows are written with `Query.setState`, not `setQueryData`, which stamps a
 * window freshly fetched and clears its invalidation: a hidden window a resync
 * marked stale would then skip its refetch when shown. No read is cancelled:
 * one that left before the change lands with it. React Query still reverts a
 * read whose last observer unmounts mid-read, returning its window to where it
 * stood before the read, without the changes written since; reads start on
 * stale windows, so that one refetches when shown.
 */
function changeWindows(qc: QueryClient, change: RowChange): void {
  for (const query of qc.getQueryCache().findAll({ queryKey: ['bookmarks'] })) {
    const window = query.state.data as BookmarkListQueryData | undefined;
    const next = window && change(window, isDefaultList(query.queryKey), false);
    if (next && next !== window) query.setState({ data: next });
  }
  correctReadsInFlight(qc, (window, key, serverCount) => change(window, isDefaultList(key), serverCount));
}

/**
 * This client's delete, ahead of the server's answer: every window drops the
 * rows it holds. A read in flight left before the request, so its server count
 * still holds them too. A failed delete is undone by reading the windows again,
 * never by writing a snapshot back over the events that arrived meanwhile.
 */
export function removeRows(qc: QueryClient, ids: ReadonlySet<string>): void {
  changeWindows(qc, (window) => {
    const items = window.items.filter((b) => !ids.has(b.id));
    return items.length === window.items.length
      ? window
      : { ...window, items, total_count: count(window) - (window.items.length - items.length) };
  });
}

/** This client's update, ahead of the server's answer, on the rows each window holds. */
export function patchRows(qc: QueryClient, ids: ReadonlySet<string>, fields: Partial<BookmarkListItem>): void {
  changeWindows(qc, (window) => (window.items.some((b) => ids.has(b.id))
    ? { ...window, items: window.items.map((b) => (ids.has(b.id) ? { ...b, ...fields } : b)) }
    : window));
}

/**
 * Apply one row event (`bookmark.created` / `.updated` / `.deleted`, an image
 * a backfill stored as `bookmark.media`, and the pipeline events) to every
 * cached bookmark window, to every read in flight, and to the row's detail.
 */
export function applyBookmarkEvent(qc: QueryClient, event: BookmarkEvent): void {
  const { id } = event;
  if (typeof id !== 'string' || !id) return;
  changeWindows(qc, rowChange(event, id));

  if (event.eventType === 'bookmark.deleted') {
    qc.removeQueries({ queryKey: ['bookmark', id], exact: true });
  } else {
    qc.invalidateQueries({ queryKey: ['bookmark', id], exact: true, refetchType: 'active' });
  }
}

/** Filtered and search windows re-run their server predicate. Only the one on
 *  screen refetches; the others are marked stale and refetch when shown. */
export function invalidateFilteredViews(qc: QueryClient): void {
  invalidateWindows(qc, (queryKey) => !isDefaultList(queryKey));
}

/** Everything the stream feeds — bookmark windows, details, sidebar counts and
 *  category metadata — after a resync or a subscription gap. */
export function reconcileBookmarkCaches(qc: QueryClient): void {
  invalidateBookmarkData(qc);
  qc.invalidateQueries({ queryKey: CATEGORY_META_QUERY_KEY, refetchType: 'active' });
}
